import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, ShieldOff, Users } from "lucide-react";
import { toast } from "sonner";
import { fetchMembers, resetMember2fa } from "@/lib/api";
import { initials } from "@/lib/format";

const apiError = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

// Mon poste (superviseurs only): reset an agent's 2FA when they lose their
// phone. Backend enforces superviseur -> agents only (403 otherwise, 400 on
// admin targets).
export const TeamSecurityCard = () => {
  const qc = useQueryClient();
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: fetchMembers });
  const agents = members.filter((m) => m.role === "agent");

  const resetMut = useMutation({
    mutationFn: (id) => resetMember2fa(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["members"] });
      toast.success("2FA réinitialisée — le membre devra la reconfigurer");
    },
    onError: (e) => toast.error(apiError(e, "Échec de la réinitialisation")),
  });

  return (
    <div data-testid="team-security-card" className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
      <div className="flex items-center gap-2 mb-1">
        <Users className="h-4 w-4 text-slate-400" />
        <p className="font-heading font-semibold text-slate-800 text-sm">Double authentification de l'équipe</p>
      </div>
      <p className="text-xs text-slate-400 mb-3">
        Si un agent perd son téléphone, réinitialisez sa 2FA ici : il la reconfigurera à sa prochaine connexion.
      </p>
      {agents.length === 0 ? (
        <p className="text-xs text-slate-400 italic" data-testid="team-security-empty">Aucun agent dans l'équipe.</p>
      ) : (
        <div className="space-y-1.5">
          {agents.map((m) => (
            <div key={m.id} data-testid={`team-security-row-${m.id}`} className="flex items-center gap-2.5 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <span className="h-7 w-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[10px] font-semibold text-slate-500 shrink-0">
                {initials(m.name)}
              </span>
              <div className="min-w-0 flex-1 leading-tight">
                <p className="text-sm font-medium text-slate-800 truncate">{m.name}</p>
                <p className="text-[11px] text-slate-400 truncate">{m.email}</p>
              </div>
              {m.totp_enabled ? (
                <>
                  <span data-testid={`team-security-badge-${m.id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md text-emerald-700 bg-emerald-50 shrink-0">
                    <ShieldCheck className="h-3 w-3" /> 2FA
                  </span>
                  <button
                    type="button"
                    data-testid={`team-security-reset-${m.id}`}
                    onClick={() => resetMut.mutate(m.id)}
                    disabled={resetMut.isPending}
                    className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium shrink-0 disabled:opacity-50"
                  >
                    <ShieldOff className="h-3.5 w-3.5" /> Réinitialiser
                  </button>
                </>
              ) : (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md text-slate-500 bg-slate-100 shrink-0">Désactivée</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
